import React from "react";
import Modal from 'react-bootstrap/Modal';
import useComponentState from "../../utils/useComponentState";
import styles from './AddTopUpsModal.module.scss';
import { InitiateTopup, submitBankTransferDetails, submitPaySlip } from "../../api/CreditTopup";
import { toast } from "react-toastify";
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const AddTopUpsModal = ({ token, show, handleClose }) => {

    const [state, setState] = useComponentState({
        loading: false,
        uploadedFile: null,
        error: {
            topupAmount: '',
            file: '',
            firstName: '',
            lastName: '',
            email: '',
            address: '',
            phoneNumber: '',
            postalCode: '',
            city: '',
            country: '',
        },
        customClass: '',
        apiResponse: {},
        formData: {
            topupAmount: '',
            file: '',
            firstName: '',
            lastName: '',
            email: '',
            address: '',
            phoneNumber: '',
            postalCode: '',
            city: '',
            country: '',
            pendingTopUpId: null,

        },
        page: 1,
    });

    const {
        loading,
        uploadedFile,
        error,
        customClass,
        apiResponse,
        formData,
        page,
    } = state;

    const handleModalClose = () => {
        setState({
            page: 1,
            formData: {
                topupAmount: '',
                file: '',
                firstName: '',
                lastName: '',
                email: '',
                address: '',
                phoneNumber: '',
                postalCode: '',
                city: '',
                country: '',
                pendingTopUpId: null,
            },
            error: {},
        });
        handleClose();
    }

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'file') {
            setState({
                formData: {
                    ...state.formData,
                    [name]: files[0],
                },
            });
        } else {
            setState({
                formData: {
                    ...state.formData,
                    [name]: value,
                },
            });
        }
    }

    const errorHandler = (field, message) => {
        setState({
            error: {
                ...state.error,
                [field]: message,
            },
        });
    }

    const handleNext = () => {

        if (state.page === 1) {
            handleTopupInitiation();
        } else if (state.page === 2) {
            handleBankTransferDetailsSubmit();
        }
    };

    const handleTopupInitiation = async () => {
        setState({ loading: true });

        try {
            if (!state.formData.topupAmount || state.formData.topupAmount <= 0) {
                errorHandler('topupAmount', 'Please enter a valid top-up amount');
                return;
            } else {
                errorHandler('topupAmount', null);
            }
            const response = await InitiateTopup(Number(state.formData.topupAmount), token);
            if (response && response?.response?.status === 'PENDING') {
                setState({ page: 2, formData: { ...state.formData, pendingTopUpId: response.response.topUpPendingId } });
                toast.success('Top-up initiated successfully');
            }

        } catch (error) {
            toast.error('Failed to initiate top-up. Please try again.');
        } finally {
            setState({ loading: false });
        }

    };

    const handleBankTransferDetailsSubmit = async () => {
        setState({ loading: true });
        try {
            let hasErrors = false;
            const newErrors = {};

            if (!state.formData.firstName) {
                newErrors.firstName = 'First name is required';
                hasErrors = true;
            }
            if (!state.formData.lastName) {
                newErrors.lastName = 'Last name is required';
                hasErrors = true;
            }
            if (!state.formData.email) {
                newErrors.email = 'Email is required';
                hasErrors = true;
            }
            if (!state.formData.address) {
                newErrors.address = 'Address is required';
                hasErrors = true;
            }
            if (!state.formData.phoneNumber) {
                newErrors.phoneNumber = 'Phone number is required';
                hasErrors = true;
            }
            if (!state.formData.postalCode) {
                newErrors.postalCode = 'Postal code is required';
                hasErrors = true;
            }
            if (!state.formData.city) {
                newErrors.city = 'City is required';
                hasErrors = true;
            }
            if (!state.formData.country) {
                newErrors.country = 'Country is required';
                hasErrors = true;
            }

            setState({ error: newErrors });

            if (hasErrors) {
                return;
            }

            const BankTransferRequest = {

                firstName: state.formData.firstName,
                lastName: state.formData.lastName,
                email: state.formData.email,
                address: state.formData.address,
                phoneNumber: state.formData.phoneNumber,
                postalCode: state.formData.postalCode,
                city: state.formData.city,
                country: state.formData.country,
                purchaseId: state.formData.pendingTopUpId,
            }

            const response = await submitBankTransferDetails(BankTransferRequest, token);
            if (response?.success) {
                toast.success('Bank transfer details submitted successfully');
                setState({ page: 3 });
            }
        } catch (error) {
            toast.error('Failed to submit bank transfer details. Please try again.');
        } finally {
            setState({ loading: false });
        }
    }

    const handleSubmitPaySlip = async () => {
        setState({ loading: true });

        try {

            if (!state.formData.file) {
                errorHandler('file', 'Please upload the pay slip file');
                return;
            } else {
                errorHandler('file', null);
            }

            const response = await submitPaySlip(
                state.formData.file,
                token,
                state.formData.pendingTopUpId
            );

            if (response && response?.response === "PAYMENT_SLIP_SENT_SUCCESSFULLY") {
                toast.success('Pay slip submitted successfully');
                handleModalClose();
            }

        } catch (error) {
            toast.error('Failed to submit pay slip. Please try again.');
        } finally {
            setState({ loading: false });
        }

    }

    const renderInput = ({
        id,
        label,
        type = 'text',
        placeholder,
        value,
        name,
        onChange,
    }) => {
        const fieldError = state.error?.[name];
        return (
            <div className={styles.formGroup}>
                <label htmlFor={id}>{label}</label>
                {type === 'file' ? (
                    <input
                        type="file"
                        id={id}
                        name={name}
                        className={`${styles.inputField} ${fieldError ? styles.inputError : ''}`}
                        onChange={onChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                ) : (
                    <input
                        type={type}
                        id={id}
                        name={name}
                        className={`${styles.inputField} ${fieldError ? styles.inputError : ''}`}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                    />
                )}
                {fieldError && <small className={styles.errorText}>{fieldError}</small>}
            </div>
        );
    };

    return (
        <Modal
            show={show}
            onHide={() => handleModalClose()}
            className={styles.addTopUpsModal}
            size='lg'
            centered
            backdrop='static'
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>Add Top-Ups</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                {state.page === 1 && (renderInput({
                    id: 'topupAmount',
                    label: 'Top-Up Amount : ',
                    type: 'number',
                    placeholder: 'Enter Top-Up Amount',
                    value: state.formData.topupAmount,
                    name: 'topupAmount',
                    onChange: handleInputChange,
                }))
                }

                {state.page === 2 && (
                    <div>
                        <div className={styles.formRow}>
                            {renderInput({
                                id: 'firstName',
                                label: 'First Name:',
                                type: 'text',
                                placeholder: 'Enter First Name',
                                value: state.formData.firstName,
                                name: 'firstName',
                                onChange: handleInputChange,
                            })}
                            {renderInput({
                                id: 'lastName',
                                label: 'Last Name:',
                                type: 'text',
                                placeholder: 'Enter Last Name',
                                value: state.formData.lastName,
                                name: 'lastName',
                                onChange: handleInputChange,
                            })}
                        </div>
                        {renderInput({
                            id: 'email',
                            label: 'Email:',
                            type: 'email',
                            placeholder: 'Enter Email',
                            value: state.formData.email,
                            name: 'email',
                            onChange: handleInputChange,
                        })}
                        {renderInput({
                            id: 'address',
                            label: 'Address:',
                            type: 'text',
                            placeholder: 'Enter Address',
                            value: state.formData.address,
                            name: 'address',
                            onChange: handleInputChange,
                        })}
                        <div className={styles.formRow}>
                            {renderInput({
                                id: 'postalCode',
                                label: 'Postal Code:',
                                type: 'text',
                                placeholder: 'Enter Postal Code',
                                value: state.formData.postalCode,
                                name: 'postalCode',
                                onChange: handleInputChange,
                            })}
                            {renderInput({
                                id: 'phoneNumber',
                                label: 'Phone Number:',
                                type: 'text',
                                placeholder: 'Enter Phone Number',
                                value: state.formData.phoneNumber,
                                name: 'phoneNumber',
                                onChange: handleInputChange,
                            })}
                        </div>
                        <div className={styles.formRow}>
                            {renderInput({
                                id: 'city',
                                label: 'City:',
                                type: 'text',
                                placeholder: 'Enter City',
                                value: state.formData.city,
                                name: 'city',
                                onChange: handleInputChange,
                            })}
                            {renderInput({
                                id: 'country',
                                label: 'Country:',
                                type: 'text',
                                placeholder: 'Enter Country',
                                value: state.formData.country,
                                name: 'country',
                                onChange: handleInputChange,
                            })}
                        </div>
                    </div>
                )}

                {state.page === 3 && (
                    <>
                        {renderInput({
                            id: 'fileUpload',
                            label: 'Upload Pay Slip:',
                            type: 'file',
                            placeholder: '',
                            name: 'file',
                            onChange: handleInputChange,
                        })}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {state.page > 1 && (
                    <button
                        className="rs-btn rs-btn-secondary"
                        onClick={() => { setState({ page: state.page - 1 }) }}
                    >
                        Back
                    </button>
                )}
                {state.page < 3 && (
                    <button
                        className="rs-btn rs-btn-primary"
                        onClick={handleNext}
                    >
                                                {state.loading ? (
                                            <>
                                              <FontAwesomeIcon icon={faSpinner} spin className='mr-2' />
                                                Processing...
                                            </>
                                          ) : (
                                            <>Next</>
                                          )}
                    </button>
                )}
                {state.page === 3 && (
                    <button
                        className="rs-btn rs-btn-primary"
                        onClick={handleSubmitPaySlip}
                    >
                        {state.loading ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className='mr-2' />
                                Submitting...
                            </>
                          ) : (
                            <>Submit Pay Slip</>
                          )}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    )

}

export default AddTopUpsModal;